<template>
    <div>
        <slot v-bind="{ open }"></slot>

        <Modal v-if="isOpen" class="SelectContentsModal" size="lg" @close="close">
            <div class="mt-5 mb-4 flex items-center">
                <h3 class="m-0 flex-1">
                    Vælg opslag
                </h3>
                <div v-if="channel" class="text-xs text-right">
                    Senest opdateret:
                    <span class="font-bold">{{ channel.refreshed_at | past | relative }}</span>
                    <div>
                        <button
                            type="button"
                            class="btn-link"
                            :disabled="refreshForm.busyOrSuccessful"
                            @click="refresh"
                        >
                            <span>Opdatér nu</span>
                            <span
                                class="fa fa-refresh"
                                :class="{ 'fa-spin': refreshForm.busyOrSuccessful }"
                            />
                        </button>
                    </div>
                </div>
            </div>

            <PageLoader :loading="loading">
                <div v-if="channel">
                    <ContentsGrid :contents="channel.contents" :rows="{ default: 2, sm: 3 }">
                        <template v-slot:default="{ contents }">
                            <ToggleableInput
                                v-for="content of contents"
                                :key="content.key"
                                v-model="selected"
                                :model="content"
                            >
                                <template v-slot:default="{ isToggled, toggle }">
                                    <button
                                        v-prevent-click-focus=""
                                        type="button"
                                        class="relative"
                                        @click="toggle(!isToggled)"
                                    >
                                        <ContentCard
                                            :content="content"
                                            :class="{ '--selected': isToggled }"
                                        />

                                        <span
                                            class="absolute inset-2 z-10 cursor-pointer text-right"
                                        >
                                            <CheckInput type="checkbox" :checked="isToggled" />
                                        </span>
                                    </button>
                                </template>
                            </ToggleableInput>
                        </template>
                    </ContentsGrid>

                    <div
                        class="-bottom-2 mt-4 -mb-8 -mx-4 px-4 py-3 bg-white border-gray-300 border-t relative rounded-b-lg sticky z-20"
                    >
                        <div class="flex items-center">
                            <span class="flex-1 text-xs">
                                Marker et eller flere opslag du har lavet for virksomheden, og tryk
                                derefter 'Færdig'
                            </span>
                            <button class="btn btn-success ml-4" @click="finish">
                                Færdig <i class="fa fa-check"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <div class="alert alert-danger">
                        <b>Ups!</b> Det ser ud til at du ikke har en aktiv {{ platform.name }} konto
                        tilknyttet længere.
                    </div>

                    <p class="texts-sm">
                        Før du kan færdiggøre dette job, skal vi kunne hente de seneste medier fra
                        din {{ platform.name }} konto. <br />
                        Du kan løse problemet ved at gå til dine tilknyttede medier, og tilknytte
                        den igen.
                    </p>

                    <div class="mt-8 text-center">
                        <a href="/settings/channels" class="btn btn-primary btn-xl">
                            Gå til tilknyttede medier <i class="fa fa-arrow-right"></i>
                        </a>
                    </div>
                </div>
            </PageLoader>
        </Modal>
    </div>
</template>

<style lang="less" type="text/less">
@import '~dashboard-less/variables';

.SelectContentsModal {
    @apply .p-4;
}

.SelectContentsModal .ResponsiveGrid {
    @apply items-start;
}

.SelectContentsModal .ContentCard {
    @apply rounded-lg;

    &,
    .CoverImage {
        transition: all 0.2s;
    }
}

.SelectContentsModal .ContentCard.--selected {
    border: 4px solid @brand-primary;

    .CoverImage {
        border-radius: 0.75rem;
    }
}

@media (hover: hover) {
    .SelectContentsModal .ResponsiveGrid button:hover .ContentCard:not(.--selected) {
        box-shadow: 0 0 0 4px @brand-primary-light;
    }
}
</style>

<script type="text/babel">
import CheckInput from 'dashboard-js/components/utilities/CheckInput'
import ContentCard from 'dashboard-js/components/contents/ContentCard'
import ContentsGrid from 'dashboard-js/components/contents/ContentsGrid'
import HasLoadingState from 'dashboard-js/mixins/HasLoadingState'
import Modal from 'dashboard-js/components/Modal'
import PageLoader from 'dashboard-js/components/utilities/PageLoader'
import PreventClickFocus from 'dashboard-js/directives/PreventClickFocus'
import ToggleableInput from 'dashboard-js/components/utilities/ToggleableInput'
import UserProvider from 'dashboard-js/providers/UserProvider'

export default {
    components: {
        ContentsGrid,
        CheckInput,
        ToggleableInput,
        ContentCard,
        PageLoader,
        Modal,
    },

    directives: { PreventClickFocus },

    mixins: [HasLoadingState],

    props: ['platform'],

    data() {
        return {
            influencer: null,
            isOpen: false,
            limit: null,
            selected: [],
            lastSelected: [],
            refreshForm: new SparkForm({}),
        }
    },

    computed: {
        channel() {
            return this.$get(this.influencer, 'channels', []).find(
                channel => channel.platform_id === this.platform.id
            )
        },
    },

    created() {
        this.load()
    },

    methods: {
        close() {
            this.isOpen = false
            this.selected = this.lastSelected
        },

        finish() {
            this.$emit('selected', this.selected)
            this.lastSelected = this.selected
            this.close()
        },

        load(silently = false) {
            return axios
                .get(route('api.influencer.show', UserProvider.authId()))
                .then(response => (this.influencer = response.data.data))
                .then(this.finishLoading(silently))
        },

        refresh() {
            Spark.post(route('api.channel.contents.refresh', this.channel.id), this.refreshForm)
                .then(() => this.load(true))
                .then(() => this.refreshForm.resetStatus())
                .catch(() =>
                    alert(
                        `Vi kunne ikke forbinde til din ${
                            this.platform.name
                        } konto lige nu. Prøv igen senere.`
                    )
                )
        },

        open() {
            this.isOpen = true
        },

        setLimit(limit) {
            this.limit = limit
        },
    },
}
</script>
